import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import type { ProfileSlug } from '@/lib/admin-types';

/**
 * Guard de rota baseado em perfil de uso (FEAT-V2-037 Fase A).
 *
 * Uso:
 *   <Route element={<RequireProfile slugs={['tecnico']}><TecnicoPage /></RequireProfile>} />
 *   <RequireProfile slugs={['administrativo', 'comercial']}><AdminPage /></RequireProfile>
 *
 * Semântica:
 *   - super-admin (matheus, profile=null): SEMPRE passa
 *   - user com profile.slug no allowlist: passa
 *   - user com profile fora do allowlist: redirect pro fallback (default `/`)
 *   - sem user (anônimo): redirect pro fallback (RequireAuth deveria
 *     blindar antes — defensivo)
 *
 * NÃO substitui RequireAuth — encadear: <RequireAuth><RequireProfile>...</></>
 */
export function RequireProfile({
  slugs,
  fallback = '/',
  children,
}: {
  slugs: ProfileSlug[];
  /** URL pra redirecionar quando perfil não está no allowlist. Default `/`. */
  fallback?: string;
  children: ReactNode;
}) {
  const { user, loading } = useAuth();

  // Durante boot inicial deixa renderizar nada (evita flash de redirect).
  if (loading) return null;

  if (!user) return <Navigate to={fallback} replace />;

  // Super-admin bypass.
  if (user.isSuperAdmin) return <>{children}</>;

  const slug = user.profile?.slug;
  if (slug && slugs.includes(slug)) return <>{children}</>;

  return <Navigate to={fallback} replace />;
}
